<template>
    <div class="pedidos-entregues-container">
        <v-card>
            <v-card-title class="mb-4">
                <h2>Pedidos Entregues</h2>
            </v-card-title>

            <!-- Filtros -->
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="3">
                        <v-text-field v-model="filters.cnpj" label="CNPJ do Revendedor" placeholder="XX.XXX.XXX/XXXX-XX"
                            v-mask="'##.###.###/####-##'" @input="handleFilterChange" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model="filters.razaosocial" label="Razão Social" @input="handleFilterChange" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model="filters.dtinicio" label="Data Início" type="date"
                            @input="handleFilterChange" />
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-text-field v-model="filters.dtfim" label="Data Fim" type="date"
                            @input="handleFilterChange" />
                    </v-col>
                </v-row>

                <v-row class="mb-1">
                    <v-col cols="12" md="2" class="d-flex align-center">
                        <v-btn color="primary" @click="fetchPedidosEntregues" :loading="loading" block>
                            <v-icon start>mdi-magnify</v-icon>
                            Pesquisar
                        </v-btn>
                    </v-col>

                    <v-col cols="12" md="6" class="d-flex align-center">
                        <v-btn @click="resetFilters" :loading="loading">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-col>

                    <v-col cols="12" md="4" class="d-flex align-center justify-end">
                        <v-btn :disabled="pedidos.length === 0" color="success" @click="exportToExcel"
                            :loading="exportingLoading">
                            <v-icon start>mdi-download</v-icon>
                            Exportar em Excel
                        </v-btn>
                    </v-col>
                </v-row>

                <!-- Validação de período -->
                <v-alert v-if="validationError" type="error" class="mb-4">
                    {{ validationError }}
                </v-alert>

                <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"></v-progress-linear>
            </v-card-text>



            <!-- Tabela -->
            <v-data-table :headers="headers" :items="pedidos" :items-length="meta.total" class="elevation-0"
                disable-pagination>
                <template #item.nomefantasia="{ item }">
                    <span class="font-weight-medium">{{ item.nomefantasia }}</span>
                </template>

                <template #item.percentual_atraso="{ item }">
                    <v-chip
                        :color="item.percentual_atraso > 15 ? 'error' : item.percentual_atraso > 5 ? 'warning' : 'success'"
                        text-color="white">
                        {{ item.percentual_atraso.toFixed(2) }}%
                    </v-chip>
                </template>

                <template #item.media_minutos_entrega="{ item }">
                    <span>{{ item.media_minutos_entrega.toFixed(1) }}</span>
                </template>

                <template #item.media_tempo_limite_minutos="{ item }">
                    <span>{{ item.media_tempo_limite_minutos.toFixed(1) }}</span>
                </template>

                <template #bottom>
                    <div class="d-flex justify-space-between align-center px-4 py-2">
                        <div class="d-flex align-center">
                            <span class="text-caption text-secondary mr-4">
                                Total de {{ meta.total || pedidos.length }} item{{ (meta.total || 0) !== 1 ? 's' : '' }}
                            </span>

                            <v-select v-model="perPage" :items="[10, 25, 50, 100]" label="Itens por página"
                                variant="outlined" dense hide-details style="width: 140px"
                                @update:model-value="onPerPageChange" />
                        </div>

                        <v-pagination v-if="meta.last_page > 1" :model-value="page" :length="meta.last_page"
                            :total-visible="5" @update:model-value="(newPage) => onPageChange(newPage)" rounded
                            class="my-2"></v-pagination>
                    </div>
                </template>

                <template #no-data>
                    <div class="text-center py-6">
                        <p class="text-gray-500">
                            Nenhum registro encontrado. Informe os filtros e clique em "Pesquisar"
                        </p>
                    </div>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useApi } from '@/utils/axios'
import { REVENDA_METRICAS_ROTAS } from '@/utils/apiRoutes'
import * as XLSX from 'xlsx'

// Estado
const loading = ref(false)
const exportingLoading = ref(false)
const pedidos = ref([])
const validationError = ref('')

const filters = ref({
    cnpj: '',
    razaosocial: '',
    dtinicio: '',
    dtfim: '',
})

const page = ref(1)
const perPage = ref(25)
const meta = ref({
    current_page: 1,
    per_page: 25,
    total: 0,
    last_page: 1,
})

// Headers da tabela
const headers = [
    { title: 'ID Revenda', key: 'id_revenda', align: 'center' },
    { title: 'Revenda', key: 'nomefantasia', align: 'start' },
    { title: 'Pedidos Entregues', key: 'total_pedidos_entregues', align: 'center' },
    { title: 'Pedidos Atrasados', key: 'pedidos_atrasados', align: 'center' },
    { title: 'Percentual Atraso', key: 'percentual_atraso', align: 'center' },
    { title: 'Tempo Médio (min)', key: 'media_minutos_entrega', align: 'center' },
    { title: 'Tempo Limite (min)', key: 'media_tempo_limite_minutos', align: 'center' },
]

// Método para validar filtros
const validateFilters = () => {
    validationError.value = ''

    // Validar se pelo menos um filtro de revendedor foi informado
    if (!filters.value.cnpj && !filters.value.razaosocial) {
        validationError.value = 'Informe o CNPJ ou Razão Social do revendedor'
        return false
    }

    // Validar se as datas foram informadas
    if (!filters.value.dtinicio || !filters.value.dtfim) {
        validationError.value = 'Informe a data de início e data de fim'
        return false
    }

    // Validar período máximo de 31 dias
    const dataInicio = new Date(filters.value.dtinicio)
    const dataFim = new Date(filters.value.dtfim)
    const diffTime = Math.abs(dataFim - dataInicio)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays > 31) {
        validationError.value = 'O período máximo de pesquisa não pode exceder 31 dias'
        return false
    }

    return true
}

// resetar filtros
const resetFilters = () => {
    filters.value = {
        cnpj: '',
        razaosocial: '',
        dtinicio: '',
        dtfim: '',
    }
    validationError.value = ''
    pedidos.value = []
    page.value = 1
    meta.value = {
        current_page: 1,
        per_page: 25,
        total: 0,
        last_page: 1,
    }
}

// Método para buscar pedidos entregues
const fetchPedidosEntregues = async () => {
    if (!validateFilters()) {
        return
    }

    loading.value = true
    try {
        const params = {
            ...(filters.value.cnpj && { cnpj: filters.value.cnpj }),
            ...(filters.value.razaosocial && { razaosocial: filters.value.razaosocial }),
            dtinicio: filters.value.dtinicio,
            dtfim: filters.value.dtfim,
            page: page.value,
            per_page: perPage.value,
        }

        const response = await useApi(REVENDA_METRICAS_ROTAS.PEDIDOS_ENTREGUES).fetch({
            params
        })

        const data = response.data.data || []

        // Converter valores de string para número
        pedidos.value = data.map((item) => ({
            ...item,
            percentual_atraso: parseFloat(item.percentual_atraso) || 0,
            media_minutos_entrega: parseFloat(item.media_minutos_entrega) || 0,
            media_tempo_limite_minutos: parseFloat(item.media_tempo_limite_minutos) || 0,
        }))

        page.value = response.data.current_page || 1

        meta.value = {
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 25,
            total: response.data.total || 0,
            last_page: response.data.last_page || 1,
        }
    } catch (error) {
        console.error('Erro ao buscar pedidos entregues:', error)
        validationError.value = 'Erro ao buscar dados. Tente novamente.'
        pedidos.value = []
    } finally {
        loading.value = false
    }
}

// Método para exportar dados em Excel
const exportToExcel = () => {
    if (pedidos.value.length === 0) {
        alert('Nenhum dado para exportar')
        return
    }

    exportingLoading.value = true
    try {
        // Preparar dados para exportação
        const dataToExport = pedidos.value.map((item) => ({
            'ID Revenda': item.id_revenda,
            'Revenda': item.nomefantasia,
            'Pedidos Entregues': item.total_pedidos_entregues,
            'Pedidos Atrasados': item.pedidos_atrasados,
            'Percentual Atraso (%)': item.percentual_atraso.toFixed(2),
            'Tempo Médio Entrega (min)': item.media_minutos_entrega.toFixed(1),
            'Tempo Limite (min)': item.media_tempo_limite_minutos.toFixed(1),
        }))

        // Criar workbook e worksheet
        const ws = XLSX.utils.json_to_sheet(dataToExport)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Pedidos Entregues')

        // Ajustar largura das colunas
        const colWidths = [30, 18, 18, 20, 25, 18]
        ws['!cols'] = colWidths.map((width) => ({ wch: width }))

        // Gerar arquivo
        const fileName = `pedidos-entregues-${new Date().toISOString().slice(0, 10)}.xlsx`
        XLSX.writeFile(wb, fileName)
    } catch (error) {
        console.error('Erro ao exportar para Excel:', error)
        alert('Erro ao exportar dados')
    } finally {
        exportingLoading.value = false
    }
}

// Método para lidar com mudanças nos filtros
const handleFilterChange = () => {
    page.value = 1
}

// Método para lidar com mudança de página
const onPageChange = (newPage) => {
    page.value = newPage
    fetchPedidosEntregues()
}

// Método para lidar com mudança de itens por página
const onPerPageChange = () => {
    page.value = 1
    fetchPedidosEntregues()
}

// Lifecycle
onMounted(() => {
    // Inicializar com datas padrão (últimos 7 dias)
    const today = new Date()
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)

    filters.value.dtfim = today.toISOString().slice(0, 10)
    filters.value.dtinicio = sevenDaysAgo.toISOString().slice(0, 10)
})
</script>

<style scoped>
.pedidos-entregues-container {
    padding: 20px;
}
</style>
